import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import firebase, { db } from 'config/firebase';
import { useSignInProcess } from 'hooks/useSignInProcess';
import React, { FormEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { resetUsersLoginInfo, updateUsersLoginInfo } from 'redux/actions/internal/general/users';
import { resetValidationInfo, setValidationInfo } from 'redux/actions/internal/general/validation';
import { RootState } from 'redux/reducers';
import { useLoginFlow } from 'hooks/useLoginFlow';
import EmailLogin from '../components/EmailLogin';
import { useAuth } from 'context/authentication';
import useTranslation from 'next-translate/useTranslation';
import LoginContinueOptions from '../components/LoginContinueOptions';
import { useLoginUserProviders } from 'context/login-context';

interface IEmailLoginPaperProps {
    onLogin?: (user: any) => void;
    inAppBrowserDetected: boolean;
}

const EmailLoginPaper = ({ onLogin, inAppBrowserDetected }: IEmailLoginPaperProps) => {
    const authentication = useLoginFlow();
    const signInProcess = useSignInProcess();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState<boolean>(false);
    const {
        signInWithEmailAndPassword,
        postUserToken,
        signInWithGoogle,
        loginWithCredential,
        persistCredential,
        signInWithFacebook,
    } = useAuth();
    const { t } = useTranslation('common');
    const userProviders = useLoginUserProviders();

    const usersLoginInfo = useSelector((state: RootState) => state.usersLoginInfo.data);

    const signInWithEmail = async (event: FormEvent, hasPassword: boolean, anotherProviders?: string[]) => {
        event.preventDefault();
        dispatch(resetValidationInfo('EMAIL_LOGIN', 'LOGIN_BACKEND_ERROR', 'EMAIL_PASSWORD_LOGIN'));

        if (anotherProviders?.length > 0 && !anotherProviders.includes('password')) {
            // if (anotherProviders.includes('google.com')) {
            //     continueWithGoogle();
            // } else if (anotherProviders.includes('facebook.com')) {
            //     continueWithFacebook();
            // }

            signInProcess.nextStep('RECOGNIZED');

            return;
        }

        if (!hasPassword) {
            signInProcess.nextStep('REGISTER_INFO');
        } else {
            setLoading(true);
            signInWithEmailAndPassword(usersLoginInfo.email, usersLoginInfo.password)
                .then(data => {
                    postUserToken(data.user);
                    dispatch(resetUsersLoginInfo());
                    setLoading(false);
                    onLogin?.(data.user);
                    authentication.closeLogin();
                })
                .catch((error: firebase.auth.Error) => {
                    setLoading(false);
                    console.log(error.code);
                    if (error.code === 'auth/user-not-found') {
                        signInProcess.nextStep('REGISTER_INFO');
                    } else if (error.code === 'auth/wrong-password') {
                        dispatch(
                            setValidationInfo(
                                {
                                    hasError: true,
                                    message: 'Password is invalid',
                                    errorType: 'password',
                                },
                                'LOGIN_BACKEND_ERROR'
                            )
                        );
                    } else {
                        dispatch(
                            setValidationInfo(
                                {
                                    hasError: true,
                                    message: error.message,
                                    errorType: 'other',
                                },
                                'LOGIN_BACKEND_ERROR'
                            )
                        );
                    }
                });
        }
    };

    return (
        <Paper
            sx={{
                backgroundColor: theme => theme.palette.background.paper,
                boxShadow: theme => theme.shadows[5],
                // padding: theme.spacing(4, 12),
                minWidth: '300px',
                minHeight: '50vh',
                // justifyContent: 'center',
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
            }}
            style={{ minHeight: '20vh' }}
        >
            <Box
                sx={{
                    width: { xs: '300px', sm: '500px' },
                    paddingTop: { xs: '20px', sm: '50px' },
                    paddingBottom: { xs: '20px', sm: '50px' },
                    paddingLeft: { xs: '20px', sm: '50px' },
                    paddingRight: { xs: '20px', sm: '50px' },
                }}
            >
                <Grid textAlign="center" alignItems="center" direction="column">
                    <Grid item xs={12}>
                        <Typography variant="h5" component="h3">
                            {t('signIn')}
                        </Typography>
                    </Grid>
                </Grid>
                <EmailLogin onContinue={signInWithEmail} loading={loading} />

                {!inAppBrowserDetected && (
                    <>
                        {' '}
                        <Box mt={4}>
                            <Divider />
                        </Box>
                        <LoginContinueOptions onLogin={onLogin} />
                    </>
                )}
            </Box>
        </Paper>
    );
};

export default EmailLoginPaper;
